import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { forestBackgroundUrl, FOREST_TARGETS, FOREST_WORLD_HEIGHT, FOREST_WORLD_WIDTH, getStaticForestActors } from "../forest/assets";
import { getForestCoverLayout } from "../forest/layout";
import type { ForestRenderer, ForestSceneState, ForestTarget } from "../forest/types";
import type { EffectsLevel, MiningPhase } from "../state/ui-types";
import { LeafIcon } from "./Icons";
import { useScenePreload } from "./use-scene-preload";
import { useReducedMotion } from "./use-reduced-motion";
import { getPresentationPolicy } from "../state/presentation-policy";
import "../styles/forest-scene.css";

interface ForestSceneProps {
  effects: EffectsLevel;
  miningPhase: MiningPhase;
  environment: string;
  className?: string;
}

const PHASE_LABEL: Record<MiningPhase, string> = {
  UNAVAILABLE: "UNAVAILABLE", LOCKED: "LOCKED", READY: "STANDBY", STARTING: "STARTING",
  MINING: "MINING", PAUSED: "PAUSED", CANDIDATE_FOUND: "PROOF FOUND", SUBMITTING: "SUBMITTING",
  SUBMIT_RETRY: "RETRY AVAILABLE", VERIFIED: "VERIFIED", EXPIRED: "EXPIRED", ERROR: "ERROR",
};

const FIELD_NOTES: Record<ForestTarget, { name: string; note: string }> = {
  ranger: { name: "The ranger", note: "Keeper of the clearing. The ranger follows the mining station's activity." },
  altar: { name: "The stone altar", note: "Ancient stone, amber crystal. Its light follows the station's mining state." },
  deer: { name: "The woodland deer", note: "A quiet visitor, grazing at the edge of the clearing." },
  bird: { name: "The forest bird", note: "A small woodland bird resting on its favorite branch." },
};

function getRangerNote(phase: MiningPhase) {
  if (phase === "MINING") return "The ranger lifts the amber stone above the altar. Its light marks the station's mining activity.";
  if (phase === "STARTING") return "The ranger takes position beside the altar as the station prepares to begin.";
  if (phase === "PAUSED") return "The ranger rests beside the altar while mining is paused.";
  return FIELD_NOTES.ranger.note;
}

function getFieldNote(target: ForestTarget | null, phase: MiningPhase, animated: boolean) {
  if (!target) return "";
  if (target === "ranger") return getRangerNote(phase);
  if (animated && target === "bird") return "The bird stretches its wings and opens its beak, then rests on the same branch.";
  return FIELD_NOTES[target].note;
}

// Share an in-flight chunk request across StrictMode replays and multiple scenes.
let rendererModule: Promise<typeof import("../forest/renderer")> | null = null;
function loadRendererModule() {
  if (!rendererModule) {
    rendererModule = import("../forest/renderer").catch((error: unknown) => {
      rendererModule = null;
      throw error;
    });
  }
  return rendererModule;
}

export function ForestScene({ effects: preference, miningPhase, environment, className = "" }: ForestSceneProps) {
  const sceneRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const hotspotsRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<ForestRenderer | null>(null);
  const reducedMotion = useReducedMotion();
  const effects = getPresentationPolicy({ effects: preference, reducedMotion, mining: miningPhase === "MINING" }).effects;
  const currentState = useRef<ForestSceneState>({ phase: miningPhase, effects, reducedMotion });
  const visibleRef = useRef(true);
  const [rendererState, setRendererState] = useState<"loading" | "ready" | "fallback">("loading");
  const [selected, setSelected] = useState<ForestTarget | null>(null);
  const [focusedTarget, setFocusedTarget] = useState<ForestTarget | null>(null);
  const enabled = effects !== "OFF";
  const preload = useScenePreload(sceneRef);
  const active = miningPhase === "MINING";

  // Props never recreate the renderer; React is outside the animation loop.
  useEffect(() => {
    const state = { phase: miningPhase, effects, reducedMotion };
    currentState.current = state;
    engineRef.current?.setState(state);
  }, [miningPhase, effects, reducedMotion]);

  // Fit static actors and their hit areas before a newly mounted route paints.
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const host = hostRef.current;
    const world = worldRef.current;
    if (!scene || !host || !world) return;
    const resize = () => {
      // Match asynchronous renderer initialization: borders belong to the scene,
      // while all three world layers share the canvas host's inner viewport.
      const bounds = host.getBoundingClientRect();
      const width = Math.max(1, bounds.width);
      const height = Math.max(1, bounds.height);
      const layout = getForestCoverLayout(width, height);
      const transform = `translate(${layout.x}px, ${layout.y}px) scale(${layout.scale})`;
      world.style.transform = transform;
      if (hotspotsRef.current) {
        hotspotsRef.current.style.transform = transform;
        hotspotsRef.current.style.setProperty("--forest-scale", String(layout.scale));
      }
      engineRef.current?.resize(width, height);
    };
    resize();
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    resizeObserver?.observe(scene);
    window.addEventListener("resize", resize);
    let intersects = true;
    const updateVisibility = () => {
      visibleRef.current = intersects && document.visibilityState !== "hidden";
      engineRef.current?.setVisible(visibleRef.current);
    };
    const intersectionObserver = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver((entries) => {
      const entry = entries.find((item) => item.target === scene);
      if (entry) intersects = entry.isIntersecting;
      updateVisibility();
    });
    intersectionObserver?.observe(scene);
    document.addEventListener("visibilitychange", updateVisibility);
    updateVisibility();
    return () => {
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!enabled || !preload || !host) {
      setRendererState("loading");
      return;
    }
    const controller = new AbortController();
    let renderer: ForestRenderer | null = null;
    let failed = false;
    setRendererState("loading");
    const fail = () => {
      if (controller.signal.aborted) return;
      failed = true;
      const failedRenderer = renderer;
      renderer = null;
      failedRenderer?.destroy();
      if (engineRef.current === failedRenderer) engineRef.current = null;
      setRendererState("fallback");
    };
    const initialize = async () => {
      const { createForestRenderer } = await loadRendererModule();
      if (controller.signal.aborted) return;
      const result = await createForestRenderer({
        host, initialState: currentState.current, signal: controller.signal,
        onInteraction: (target) => { if (!controller.signal.aborted) setSelected(target); },
        onFailure: fail,
      });
      if (controller.signal.aborted || failed) {
        result.destroy();
        return;
      }
      renderer = result;
      engineRef.current = result;
      // An async init may have outlived several prop or visibility changes.
      result.setState(currentState.current);
      const bounds = host.getBoundingClientRect();
      result.resize(Math.max(1, bounds.width), Math.max(1, bounds.height));
      result.setVisible(visibleRef.current);
      // A guarded renderer operation can synchronously report failure here.
      if (controller.signal.aborted || failed) return;
      setRendererState("ready");
    };
    initialize().catch(fail);
    return () => {
      controller.abort();
      renderer?.destroy();
      if (engineRef.current === renderer) engineRef.current = null;
    };
  }, [enabled, preload]);

  useEffect(() => {
    if (!selected) return;
    const timeout = window.setTimeout(() => setSelected(null), 5500);
    return () => window.clearTimeout(timeout);
  }, [selected]);

  const interact = (target: ForestTarget) => {
    setSelected(target);
    engineRef.current?.interact(target);
  };
  const sceneStyle = { width: FOREST_WORLD_WIDTH, height: FOREST_WORLD_HEIGHT } as CSSProperties;
  const staticActors = getStaticForestActors(miningPhase);
  const rendered = enabled && rendererState === "ready";
  const artworkDescription = active
    ? "A green-hooded ranger raises an amber stone with both hands beside an ancient forest altar, surrounded by quiet woodland animals."
    : "An ancient stone altar rests in a mossy woodland clearing, watched over by a green-hooded ranger and forest animals.";
  const noteTarget = selected ?? focusedTarget;
  const selectedNote = selected
    ? getFieldNote(selected, miningPhase, rendered && !reducedMotion)
    : "Press Enter or Space to inspect.";
  const modeLabel = !enabled ? "Still scene" : rendererState === "fallback" ? "Still scene · animation unavailable" :
    reducedMotion ? "Reduced motion" : rendererState === "loading" ? "Preparing the clearing" : "Touch the forest";

  return (
    <section ref={sceneRef}
      className={`forest-scene scene-interactive effects-${effects.toLowerCase()} ${active ? "is-mining" : ""} ${reducedMotion ? "is-reduced-motion" : ""} ${className}`}
      aria-label="MERKLEWOOD forest mining station" data-phase={miningPhase} data-renderer={rendered ? "ready" : "static"}
    >
      <img className="forest-scene-background" src={forestBackgroundUrl} width={FOREST_WORLD_WIDTH} height={FOREST_WORLD_HEIGHT}
        alt={artworkDescription}
        fetchPriority="high" draggable={false} />
      <div ref={worldRef} className="forest-world" style={sceneStyle}>
        <div className="forest-static-actors" data-testid="forest-static-actors" hidden={rendered} aria-hidden="true">
          {staticActors.map((actor) => (
            <div key={actor.id} className="forest-static-actor" data-actor={actor.id}
              style={{
                left: actor.x, top: actor.y, width: actor.width, height: actor.height,
                transform: actor.transformMatrix ? `matrix(${actor.transformMatrix.join(",")},0,0)` : undefined,
              }}>
              <img src={actor.src} alt="" draggable={false} style={{
                width: actor.atlas.width * actor.width / actor.frame.width,
                height: actor.atlas.height * actor.height / actor.frame.height,
                left: -actor.frame.x * actor.width / actor.frame.width,
                top: -actor.frame.y * actor.height / actor.frame.height,
              }} />
            </div>
          ))}
        </div>
      </div>
      <div ref={hostRef} className="forest-canvas-host" aria-hidden="true" style={{ visibility: rendered ? "visible" : "hidden" }} />
      <div className="forest-scene-shade" aria-hidden="true" />
      <div ref={hotspotsRef} className="forest-hotspots" style={sceneStyle} role="group" aria-label="Forest characters and altar">
        {(Object.keys(FIELD_NOTES) as ForestTarget[]).map((target) => {
          const area = FOREST_TARGETS[target];
          return <button type="button" key={target} className="forest-hotspot" data-target={target}
            style={{ left: area.x, top: area.y, width: area.width, height: area.height }}
            onClick={() => interact(target)}
            onFocus={(event) => {
              if (event.currentTarget.matches(":focus-visible")) {
                setSelected(null);
                setFocusedTarget(target);
              }
            }}
            onBlur={() => setFocusedTarget(null)}
            title={FIELD_NOTES[target].name} aria-label={`Inspect ${FIELD_NOTES[target].name.toLowerCase()}`} />;
        })}
      </div>
      <div className="scene-topline">
        <span><LeafIcon /> MERKLEWOOD outpost <b>07</b></span>
        <span className="recon-status-tag">{environment}</span>
      </div>
      <div className="forest-field-note" role="status" aria-live="polite">
        {noteTarget && <div className="forest-note-card">
          <div><strong>{FIELD_NOTES[noteTarget].name}</strong><p>{selectedNote}</p></div>
          {selected && <button type="button" onClick={() => setSelected(null)} aria-label="Dismiss field note">×</button>}
        </div>}
      </div>
      <div className="scene-caption">
        <span className={`scene-state state-${active ? "active" : miningPhase === "PAUSED" ? "paused" : "idle"}`}>
          <i aria-hidden="true" /> {PHASE_LABEL[miningPhase]}
        </span>
        <span className="forest-motion-note">{modeLabel}</span>
      </div>
    </section>
  );
}
